import { Dispatch } from 'react';

import { ActionBar, ActionBarVariant, FAIconName } from '@equip.health/ui';

import { AssessmentAction } from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGetAssessmentsListQuery } from '~/store/services/assessments';

import { RailSectionErrorLabel } from './rightRail.constants';
import { RailSectionErrorFallback } from './RailSectionErrorFallback';

interface IntakeSurveyBannerSectionProps {
  patientId: string;
  updateAssessmentState: Dispatch<AssessmentAction>;
}

type BannerContent = {
  barIcon: FAIconName;
  buttonText?: string;
  onButtonClick?: () => void;
  text: string;
  variant: ActionBarVariant;
};

const IntakeSurveyBannerSection = ({
  patientId,
  updateAssessmentState,
}: IntakeSurveyBannerSectionProps) => {
  const { setComponentVisibility } = useComponentPortalContext();
  const { data, isError, isLoading } = useGetAssessmentsListQuery(
    { patientId, filterBy: { surveyName: SurveyName.ADMISSIONS_INTAKE } },
    { skip: !patientId },
  );

  const intakeSurvey = (data?.assessments ?? []).find(
    ({ status }) => status !== SurveyResponseStatus.VOID,
  );

  const handleViewResults = () => {
    updateAssessmentState({
      data: intakeSurvey,
      field: 'selectedSurveyResults',
    });
  };

  const handleOpenSurvey = () => {
    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      data: intakeSurvey,
      isExpanded: true,
      isVisible: true,
    });
  };

  const getContent = (): BannerContent => {
    switch (intakeSurvey?.status) {
      case SurveyResponseStatus.DONE:
        return {
          barIcon: FAIconName.checkRegular,
          buttonText: 'View results',
          onButtonClick: handleViewResults,
          text: 'Intake survey complete',
          variant: 'primary',
        };
      case SurveyResponseStatus.REQUESTED:
        return {
          barIcon: FAIconName.fileUserRegular,
          buttonText: 'Begin now',
          onButtonClick: handleOpenSurvey,
          text: 'Intake survey not started',
          variant: 'warning',
        };
      case SurveyResponseStatus.IN_PROGRESS:
      case SurveyResponseStatus.INCOMPLETE:
        return {
          barIcon: FAIconName.fileUserRegular,
          buttonText: 'Resume',
          onButtonClick: handleOpenSurvey,
          text: 'Intake survey not complete',
          variant: 'warning',
        };
      default:
        return {
          barIcon: FAIconName.fileUserRegular,
          text: 'Intake survey not yet created',
          variant: 'warning',
        };
    }
  };

  if (isError) {
    return (
      <RailSectionErrorFallback
        id="patient-overview-right-rail-v2__intake-banner__error"
        label={RailSectionErrorLabel.IntakeBanner}
      />
    );
  }
  if (isLoading) return null;

  const { barIcon, buttonText, onButtonClick, text, variant } = getContent();

  return (
    <ActionBar
      barIcon={barIcon}
      buttonIcon={FAIconName.chevronRightRegular}
      buttonIconPlacement="end"
      buttonText={buttonText}
      hasShadow
      id="patient-overview-right-rail-v2__intake-banner"
      onButtonClick={onButtonClick}
      size="small"
      text={text}
      textVariant="captionMedium"
      variant={variant}
    />
  );
};

export default IntakeSurveyBannerSection;
